import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function formatMeetDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") {
    return { month: "TBD", day: "" };
  }

  const parts = dateStr.split("-");
  if (parts.length < 3) {
    return { month: "TBD", day: "" };
  }

  const monthIndex = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  if (!MONTHS[monthIndex] || Number.isNaN(day)) {
    return { month: "TBD", day: "" };
  }

  return { month: MONTHS[monthIndex], day: String(day) };
}

function formatMeetCountdown(dateStr) {
  if (!dateStr || typeof dateStr !== "string") {
    return null;
  }

  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) {
    return null;
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffMs = target.getTime() - startOfToday.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < -14) {
    return `${Math.ceil(-diffDays / 7)} weeks ago`;
  }
  if (diffDays < -1) {
    return `${-diffDays} days ago`;
  }
  if (diffDays === -1) {
    return "yesterday";
  }
  if (diffDays === 0) {
    return "today";
  }
  if (diffDays <= 1) {
    return `tomorrow`;
  }
  if (diffDays <= 14) {
    return `+ ${diffDays} days`;
  }
  return `+ ${Math.ceil(diffDays / 7)} weeks`;
}

function sortMeetDateValue(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return Number.POSITIVE_INFINITY;
  const parsed = new Date(`${dateStr}T00:00:00`);
  const time = parsed.getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

function getAthleteCount(meet) {
  if (!meet || typeof meet !== "object") return null;
  if (Array.isArray(meet.athletes)) return meet.athletes.length;
  return (
    meet.athlete_count ??
    meet.athletes_count ??
    meet.athleteCount ??
    null
  );
}

function Home() {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [meets, setMeets] = useState([]);
  const navigate = useNavigate();
  const apiBase = useMemo(() => getApiBase(), []);
  const sortedMeets = useMemo(() => {
    const list = Array.isArray(meets) ? [...meets] : [];
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    ).getTime();
    list.sort((a, b) => {
      const aTime = sortMeetDateValue(a?.date);
      const bTime = sortMeetDateValue(b?.date);
      const aIsToday = aTime === startOfToday;
      const bIsToday = bTime === startOfToday;
      if (aIsToday !== bIsToday) {
        return aIsToday ? -1 : 1;
      }
      if (aTime === bTime) {
        const aName = (a?.name || "").toLowerCase();
        const bName = (b?.name || "").toLowerCase();
        return aName.localeCompare(bName);
      }
      return bTime - aTime;
    });
    return list;
  }, [meets]);

  useEffect(() => {
    let isActive = true;

    const loadMeets = async () => {
      setIsLoading(true);
      setError("");
      setStatus("");

      try {
        const response = await fetch(`${apiBase}/meet`, {
          method: "GET",
          credentials: "include",
        });

        if (response.status === 401) {
          navigate("/login", { replace: true });
          return;
        }

        const data = await response.json();
        if (!response.ok || data?.error) {
          if (isActive) {
            navigate("/login", { replace: true });
          }
          return;
        }

        if (isActive) {
          setMeets(Array.isArray(data.meets) ? data.meets : []);
        }
      } catch (_err) {
        if (isActive) {
          navigate("/login", { replace: true });
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadMeets();

    return () => {
      isActive = false;
    };
  }, [apiBase, navigate]);

  const handleLogout = async () => {
    setIsBusy(true);
    setError("");
    setStatus("Signing out...");

    try {
      const response = await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Logout failed.");
      }

      navigate("/login", { replace: true });
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div>
          <p className="dashboard-title">MEET DAY STRATEGY</p>
          <p className="subtle">Your Meets</p>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Add meet"
          onClick={() => navigate("/meet/new")}
        >
          +
        </button>
      </header>

      <section className="meet-list">
        {isLoading ? (
          <div className="status">Loading meets...</div>
        ) : meets.length === 0 ? (
          <div className="empty-state">
            <h3>No meets yet</h3>
            <p>Add your first meet to get started.</p>
          </div>
        ) : (
          sortedMeets.map((meet, index) => {
            const { month, day } = formatMeetDate(meet.date);
            const athleteCount = getAthleteCount(meet);
            const countdown = formatMeetCountdown(meet.date);
            return (
              <article
                className="meet-card"
                key={meet.id || meet.name || `meet-${index}`}
              >
                <div className="meet-meta">
                  <span className="meet-date" title={day+" "+month}>
                    {countdown}
                  </span>
                  {athleteCount !== null ? (
                    <span className="meet-athletes">{athleteCount} Athletes</span>
                  ) : null}
                </div>
                <div className="meet-main">
                  <div className="meet-title-row">
                    <button
                      type="button"
                      className="meet-name-link"
                      onClick={() =>
                        meet.id
                          ? navigate(`/meet/${meet.id}/runsheet`)
                          : navigate("/meet/new")
                      }
                    >
                      {meet.name || "Untitled meet"}
                    </button>
                    <button
                      type="button"
                      className="icon-button ghost"
                      aria-label="Edit meet"
                      onClick={() => navigate(`/meet/${meet.id}`)}
                    >
                      ✎
                    </button>
                  </div>
                  {meet.link ? (
                    <a
                      className="meet-link"
                      href={meet.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="link-icon" aria-hidden="true">
                        ↗
                      </span>
                      liftingcast
                    </a>
                  ) : null}
                </div>
              </article>
            );
          })
        )}
      </section>

      <div className="dashboard-actions">
        <button type="button" className="link-button" onClick={handleLogout} disabled={isBusy}>
          {isBusy ? "Signing out..." : "Log out"}
        </button>
      </div>

      {status ? <div className="status">{status}</div> : null}
      {error ? <div className="error">{error}</div> : null}
    </div>
  );
}

export default Home;
